import { Issue, IssueGroup } from '../../api/issues';
import { RequeuePreview } from '../data/cockpit';
import { Owner } from '../components/ui';

/** Who has to act on one issue: us (also when it needs offering again), the customer, or nobody. */
export const ownerOfIssue = (i: Pick<Issue, 'status'>): Owner => {
    switch (i.status) {
        case 'WaitingOnUs':
        case 'NeedsRequeue':
            return 'us';
        case 'WaitingOnCustomer':
            return 'cu';
        default:
            return 'au';
    }
};

/** Who has to act on a kind of problem: whoever has the most open ones waiting on them. */
export const ownerOfGroup = (g: Pick<IssueGroup, 'waitingOnUs' | 'waitingOnCustomer' | 'needsRequeue'>): Owner => {
    if (g.waitingOnUs + g.needsRequeue > 0 && g.waitingOnUs + g.needsRequeue >= g.waitingOnCustomer) return 'us';
    if (g.waitingOnCustomer > 0) return 'cu';
    return 'au';
};

export const isOpen = (i: Pick<Issue, 'status'>) => i.status !== 'Resolved' && i.status !== 'Closed';

/**
 * The requeue column: a short label and its colour, and the reason apart. The reason is a sentence and goes on its
 * own line under the label; in the label it ran out of every table.
 */
export const verdictLook = (p?: RequeuePreview): { text: string; cls: string; reason?: string } => {
    if (!p) return { text: '…', cls: 'chip' };
    switch (p.verdict) {
        case 'auto':
            return { text: p.nextAt ? `Vanzelf · ${new Date(p.nextAt).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Amsterdam' })}` : 'Vanzelf', cls: 'own au' };
        case 'may':
            return { text: 'Mag', cls: 'chip ok' };
        case 'no':
            return { text: 'Tegengehouden', cls: 'chip bad', reason: p.reason ?? undefined };
        case 'na':
            return { text: 'Helpt niet', cls: 'chip' };
        default:
            return { text: 'Nog niet nagekeken', cls: 'chip warn' };
    }
};

/** The problem code of an object the connector never received: it has no attempts, only a gap. */
export const NEVER_ARRIVED = 'never-arrived';

/** The attempts of an issue as a label; nothing when there were none, "Nooit ontvangen" for an object that never came. */
export const attemptsLabel = (i: Pick<Issue, 'attempts' | 'code'>): string | undefined =>
    i.code === NEVER_ARRIVED ? 'Nooit ontvangen' : i.attempts > 0 ? `${i.attempts} ${i.attempts === 1 ? 'poging' : 'pogingen'}` : undefined;

/** Whether the object is in Exact, in words; the enum value never reaches the screen. */
export const exactStateLabel = (state?: string | null): string => {
    switch (state) {
        case 'InExact':
            return 'ja';
        case 'NotInExact':
            return 'nee';
        default:
            return 'onbekend';
    }
};

/** The document an issue is about, as people call it. */
export const issueLabel = (i: Pick<Issue, 'invoiceId' | 'relationId' | 'objectType'>) => i.invoiceId || i.relationId;

const OBJECT_NAMES: Record<string, string> = {
    Customer: 'klant',
    Invoice: 'factuur',
    InvoiceSalesEntry: 'factuur',
    CreditNote: 'creditnota',
    CreditNoteSalesEntry: 'creditnota',
    PaymentOnline: 'betaling',
    PaymentSalesEntry: 'betaling',
    PaymentOnlineCredit: 'terugbetaling',
    Refund: 'terugbetaling',
};

export const objectName = (objectType?: string | null) => (objectType ? OBJECT_NAMES[objectType] ?? objectType : '');

/** offer and fetch go through the requeue dialog; relink and link open the dialog that compares with Exact. */
export type NextAction = 'offer' | 'fetch' | 'relink' | 'link';

/** Whether the action is offering the message (again), the only kind the requeue dialog does. */
export const isOffer = (action?: NextAction) => action === 'offer' || action === 'fetch';

/** What the issue panel says has to happen now, from where the issue stands - never from the generic text of its code. */
export interface NextStep {
    title: string;
    /** Who acts: drives the colour and the badge. */
    who: Owner | 'done';
    /** The concrete situation of this issue, from its checks; the generic explanation only when there is none. */
    text?: string;
    action?: NextAction;
    actionLabel?: string;
}

type NextInput = Pick<Issue, 'status' | 'resolution' | 'autoAction' | 'interpretation'> & Partial<Pick<Issue, 'canChooseAccount'>>;

const clockOf = (at?: string | null) =>
    at ? new Date(at).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Amsterdam' }) : undefined;

/**
 * The next step of one issue. The requeue verdict decides it, because that is what the dashboard will or will not do;
 * the steps of the code are background and may describe another case (a payment is never offered by itself).
 */
export const nextStep = (i: NextInput, preview?: RequeuePreview): NextStep => {
    const text = i.resolution || i.interpretation?.explanation || undefined;
    const fetch = i.autoAction === 'Replay';

    if (i.status === 'Resolved' || i.status === 'Closed') return { title: 'Opgelost', who: 'done', text };

    // A link or relink is decided by a person in its own dialog, never by offering the message: its button goes first,
    // also when the requeue preview says no (it does, and points at this very button).
    if (i.autoAction === 'Link') {
        return { title: 'Koppel de boeking die Exact al heeft', who: 'us', text, action: 'link', actionLabel: 'Koppelen' };
    }
    if (i.autoAction === 'Relink' || i.canChooseAccount) {
        return { title: 'Kies de juiste relatie in Exact', who: i.autoAction === 'Relink' ? 'us' : ownerOfIssue(i), text, action: 'relink', actionLabel: 'Relatie kiezen' };
    }

    switch (preview?.verdict) {
        case 'may':
            return {
                title: fetch ? 'Keur ophalen uit Chargebee goed' : 'Keur opnieuw aanbieden goed',
                who: 'us',
                text,
                action: fetch ? 'fetch' : 'offer',
                actionLabel: fetch ? 'Ophalen uit Chargebee en aanbieden' : 'Opnieuw aanbieden',
            };
        case 'auto': {
            const at = clockOf(preview.nextAt);
            return { title: at ? `Gaat vanzelf · ${at}` : 'Gaat vanzelf', who: 'au', text };
        }
        case 'no':
            // The reason says what Exact has and what to do; the issue's own text may still describe the proposal the
            // check just refused, and the two side by side contradicted each other.
            return { title: 'Niet opnieuw aanbieden', who: ownerOfIssue(i) === 'cu' ? 'cu' : 'us', text: preview.reason ?? text };
        default:
            break;
    }

    // No button below these titles: they say who acts, never name an action the panel does not have.
    const owner = ownerOfIssue(i);
    if (owner === 'cu') return { title: 'De klant moet iets doen', who: 'cu', text };
    if (owner === 'au') return { title: 'Lost zichzelf op', who: 'au', text };
    return { title: 'Wij moeten iets doen', who: 'us', text };
};

/** Where the issue panel finds an issue at once: filled by the list before it opens the panel, and by the panel itself. */
export const issueCacheKey = (customer: string, environment: string, key: string) => `issue/${customer}/${environment}/${key}`;
